
import { mapGetters } from "vuex";
import { restauranteFeatureFlags, restauranteTempoLimites } from "../../config";
import { formatElapsedMinutes, quantityLabel } from "./kdsWorkspace";

export default {
  name: "app-restaurante-producao",
  props: {
    tipoProducao: {
      type: String,
      default: null,
    },
    titulo: {
      type: String,
      default: "Produção",
    },
    subtitulo: {
      type: String,
      default: "Cozinha e bar",
    },
    exibirMesasAtalho: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      itens: [],
      setores: [],
      autoRefresh: true,
      refreshInterval: null,
      lastRefreshAt: null,
      featureFlags: restauranteFeatureFlags,
      restauranteProducaoEtapaUnica: false,
      restauranteUsarApenasComandas: false,
      expandedTickets: {},
      activeColumn: "pendente",
      painelLoading: false,
      painelRequestInFlight: false,
      requestSequence: 0,
      updatingTicketKeys: {},
      updatingItemIds: {},
      liveMessage: "",
      visibilityHandler: null,
      focusHandler: null,
      fullscreenHandler: null,
      isFullscreen: false,
      filters: {
        setor_producao_id: null,
      },
    };
  },
  computed: {
    ...mapGetters(["itsblocked"]),
    statusColumns() {
      if (this.restauranteProducaoEtapaUnica) {
        return [{ status: "pronto", label: "Produção" }];
      }

      return [
        { status: "pendente", label: "Pendente" },
        { status: "preparando", label: "Preparando" },
        { status: "pronto", label: "Pronto" },
      ];
    },
    setorOptions() {
      return [
        { value: null, text: "Todos" },
        ...this.setores
          .filter(
            (setor) => !this.tipoProducao || setor.tipo === this.tipoProducao
          )
          .map((setor) => ({
            value: setor.id,
            text: `${setor.nome} (${this.tipoLabel(setor.tipo)})`,
          })),
      ];
    },
    allTickets() {
      return this.statusColumns.flatMap((column) =>
        this.ticketsPorColuna(column)
      );
    },
    totalTicketsLabel() {
      return `${quantityLabel(
        this.allTickets.length,
        "ticket",
        "tickets"
      )} no painel`;
    },
    atrasadosTickets() {
      return this.allTickets.filter((ticket) => ticket.atrasado).length;
    },
    overdueTicketsLabel() {
      return quantityLabel(this.atrasadosTickets, "atrasado", "atrasados");
    },
    lastRefreshLabel() {
      if (!this.lastRefreshAt) {
        return "aguardando primeira carga";
      }

      return `Atualizado ${this.lastRefreshAt.toLocaleTimeString("pt-BR", {
        hour: "2-digit",
        minute: "2-digit",
      })}`;
    },
  },
  mounted() {
    if (this.itsblocked) {
      this.$router.push({ name: "assinatura" });
      return;
    }

    this.getInit();
    this.visibilityHandler = () => {
      if (!document.hidden && this.autoRefresh) {
        this.getPainel(false);
      }
    };
    this.focusHandler = () => {
      if (!document.hidden && this.autoRefresh) {
        this.getPainel(false);
      }
    };
    this.fullscreenHandler = () => {
      this.isFullscreen =
        document.fullscreenElement === document.documentElement;
    };
    document.addEventListener("visibilitychange", this.visibilityHandler);
    document.addEventListener("fullscreenchange", this.fullscreenHandler);
    window.addEventListener("focus", this.focusHandler);
    this.fullscreenHandler();
    this.refreshInterval = setInterval(() => {
      // O painel visual pausa quando a aba esta em segundo plano.
      if (this.autoRefresh && !document.hidden) {
        this.getPainel(false);
      }
    }, 15000);
  },
  beforeDestroy() {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
    }
    if (this.visibilityHandler) {
      document.removeEventListener("visibilitychange", this.visibilityHandler);
    }
    if (this.fullscreenHandler) {
      document.removeEventListener("fullscreenchange", this.fullscreenHandler);
    }
    if (this.focusHandler) {
      window.removeEventListener("focus", this.focusHandler);
    }
  },
  methods: {
    async toggleFullscreen() {
      try {
        if (document.fullscreenElement === document.documentElement) {
          await document.exitFullscreen();
          return;
        }

        if (!document.documentElement?.requestFullscreen) {
          this.$toast.warning(
            "A tela cheia não está disponível neste navegador."
          );
          return;
        }

        await document.documentElement.requestFullscreen();
      } catch (error) {
        this.$toast.warning("Não foi possível abrir o KDS em tela cheia.");
      }
    },
    async getInit() {
      this.painelLoading = true;
      try {
        await this.getConfiguracoesRestaurante();
        await Promise.all([this.getSetores(), this.getPainel(false)]);
      } finally {
        this.painelLoading = false;
      }
    },
    async getConfiguracoesRestaurante() {
      try {
        const { data } = await this.$http.post("/configuracoesgerais/find");
        const configuracoes = Array.isArray(data) ? data[0] : data;

        this.restauranteProducaoEtapaUnica =
          configuracoes?.restaurante_producao_etapa_unica ?? false;
        this.restauranteUsarApenasComandas =
          configuracoes?.restaurante_usar_apenas_comandas ?? false;

        if (this.restauranteProducaoEtapaUnica) {
          this.activeColumn = "pronto";
        }
      } catch (error) {
        this.restauranteProducaoEtapaUnica = false;
        this.restauranteUsarApenasComandas = false;
      }
    },
    async getSetores() {
      try {
        const { data } = await this.$http.post(
          "/restaurante/setores-producao/select"
        );
        this.setores = data;
      } catch (error) {
        this.$toast.error("Não foi possível carregar os setores.");
      }
    },
    async getPainel(showLoading = true) {
      if (this.painelRequestInFlight) return;
      this.painelRequestInFlight = true;
      const requestId = ++this.requestSequence;
      try {
        if (showLoading) {
          this.painelLoading = true;
        }

        const payload = {};

        if (this.filters.setor_producao_id) {
          payload.setor_producao_id = this.filters.setor_producao_id;
        }

        if (this.tipoProducao) {
          payload.tipo = this.tipoProducao;
        }

        const { data } = await this.$http.post(
          "/restaurante/producao/painel",
          payload
        );
        if (requestId !== this.requestSequence) {
          return;
        }

        this.itens = Array.isArray(data) ? data : [];
        this.lastRefreshAt = new Date();
        this.liveMessage = `Produção atualizada: ${this.totalTicketsLabel}.`;
      } catch (error) {
        this.$toast.error("Não foi possível carregar a produção.");
      } finally {
        this.painelRequestInFlight = false;
        if (showLoading && requestId === this.requestSequence) {
          this.painelLoading = false;
        }
      }
    },
    async updateStatus(item, status) {
      if (this.isItemUpdating(item)) return;
      this.$set(this.updatingItemIds, item.id, true);
      try {
        await this.$http.post("/restaurante/producao/update-status", {
          id: item.id,
          status,
        });
        const message = `${this.produtoDescricao(item)}: ${this.actionLabel(
          status
        ).toLowerCase()}.`;
        this.$toast.success(message);
        this.liveMessage = message;
        await this.getPainel(false);
      } catch (error) {
        const message =
          error.response && error.response.data && error.response.data.msg
            ? error.response.data.msg
            : "Não foi possível atualizar o status.";
        this.$toast.error(message);
      } finally {
        this.$delete(this.updatingItemIds, item.id);
      }
    },
    itensPorStatus(status) {
      return this.itens.filter((item) => item.status === status);
    },
    itensPorColuna(column) {
      if (this.restauranteProducaoEtapaUnica) {
        return this.itens;
      }

      return this.itensPorStatus(column.status);
    },
    ticketsPorColuna(column) {
      const tickets = {};

      this.itensPorColuna(column).forEach((item) => {
        const key = `${
          this.restauranteProducaoEtapaUnica ? "etapa-unica" : column.status
        }:${this.ticketKey(item)}`;

        if (!tickets[key]) {
          tickets[key] = {
            key,
            status: item.status,
            firstItem: item,
            items: [],
            atencao: false,
            atrasado: false,
          };
        }

        tickets[key].items.push(item);

        if (
          this.itemTimestamp(item) < this.itemTimestamp(tickets[key].firstItem)
        ) {
          tickets[key].firstItem = item;
        }

        tickets[key].atencao = tickets[key].atencao || this.isItemAtencao(item);
        tickets[key].atrasado =
          tickets[key].atrasado || this.isItemAtrasado(item);
      });

      const ticketsOrdenados = Object.values(tickets).map((ticket) => ({
        ...ticket,
        status: this.ticketStatus(ticket.items),
        items: ticket.items.sort(
          (first, second) =>
            this.itemTimestamp(first) - this.itemTimestamp(second)
        ),
        comandas: this.groupTicketItemsByComanda(ticket.items),
      }));

      return ticketsOrdenados.sort((first, second) => {
        const firstTimestamp = this.itemTimestamp(first.firstItem);
        const secondTimestamp = this.itemTimestamp(second.firstItem);
        const mostrarRecentesPrimeiro =
          !this.restauranteProducaoEtapaUnica && column.status === "pronto";

        return mostrarRecentesPrimeiro
          ? secondTimestamp - firstTimestamp
          : firstTimestamp - secondTimestamp;
      });
    },
    ticketStatus(items) {
      const itemEmPreparo = items.find((item) => item.status !== "pronto");

      return itemEmPreparo ? itemEmPreparo.status : "pronto";
    },
    ticketNextStatus(ticket) {
      if (this.restauranteProducaoEtapaUnica) {
        return ticket.items.some((item) => item.status !== "pronto")
          ? "pronto"
          : "entregue";
      }

      return this.nextStatus(ticket.status);
    },
    nextStatus(status) {
      const next = {
        pendente: "preparando",
        preparando: "pronto",
        pronto: "entregue",
      };

      return next[status] || null;
    },
    ticketKey(item) {
      return [
        item.atendimento_id || "atendimento",
        item.mesa?.id || item.mesa_id || "sem-mesa",
        item.setor_producao_id || item.setor_producao?.id || "sem-setor",
        this.ticketBatchKey(item),
      ].join(":");
    },
    ticketBatchKey(item) {
      const value =
        item.ticket_key ||
        item.pedido_producao_id ||
        item.lote_producao_id ||
        item.enviado_at ||
        item.item?.enviado_producao_at ||
        item.created_at ||
        item.id;

      return String(value || item.id || "sem-envio")
        .replace("T", " ")
        .slice(0, 19);
    },
    itemComandaKey(item) {
      return (
        item.comanda?.id ||
        item.item?.comanda?.id ||
        item.comanda_id ||
        item.item?.comanda_id ||
        "sem-comanda"
      );
    },
    itemComandaLabel(item) {
      const comanda = item.comanda || item.item?.comanda || null;

      if (this.restauranteUsarApenasComandas && comanda?.tipo === "avulso") {
        return this.comandaModoLabel(comanda);
      }

      if (comanda?.tipo === "avulso" || (!comanda?.nome && comanda?.codigo)) {
        return `Avulsa ${comanda.codigo || comanda.id}`;
      }

      const nome =
        item.comanda?.nome ||
        item.item?.comanda?.nome ||
        item.item?.comanda_nome ||
        "";
      const numero =
        item.comanda?.numero ||
        item.item?.comanda?.numero ||
        item.comanda_numero ||
        "";

      if (nome) {
        return nome;
      }

      return numero ? `Comanda ${numero}` : "Sem comanda";
    },
    comandaModoLabel(comanda) {
      const nome = String(comanda?.nome || "").trim();
      const codigo = String(comanda?.codigo || "").trim();

      if (nome && codigo) {
        return `Comanda ${nome} (${codigo})`;
      }

      if (nome) {
        return `Comanda ${nome}`;
      }

      if (codigo) {
        return `Comanda ${codigo}`;
      }

      return comanda?.id ? `Comanda #${comanda.id}` : "Comanda";
    },
    groupTicketItemsByComanda(items) {
      const grupos = {};

      items.forEach((item) => {
        const key = this.itemComandaKey(item);

        if (!grupos[key]) {
          grupos[key] = {
            key,
            label: this.itemComandaLabel(item),
            items: [],
          };
        }

        grupos[key].items.push(item);
      });

      return Object.values(grupos);
    },
    isTicketExpanded(ticket) {
      if (
        Object.prototype.hasOwnProperty.call(this.expandedTickets, ticket.key)
      ) {
        return this.expandedTickets[ticket.key];
      }

      return ticket.status !== "pronto";
    },
    toggleTicket(ticket) {
      this.$set(
        this.expandedTickets,
        ticket.key,
        !this.isTicketExpanded(ticket)
      );
    },
    async updateTicketGroupStatus(ticket, status) {
      if (this.isTicketUpdating(ticket)) return;
      this.$set(this.updatingTicketKeys, ticket.key, true);
      try {
        const itens = ticket.items.filter((ticketItem) =>
          this.canTransitionTo(ticketItem.status, status)
        );

        if (!itens.length) {
          return;
        }

        if (this.featureFlags.kdsAvancado) {
          try {
            await this.$http.post("/restaurante/producao/bump", {
              ticket_key: ticket.key,
              ids: itens.map((ticketItem) => ticketItem.id),
              status,
            });
          } catch (error) {
            await this.updateTicketStatusFallback(itens, status);
          }
        } else {
          await this.updateTicketStatusFallback(itens, status);
        }

        const message = `${this.itemMesaLabel(
          ticket.firstItem
        )}: ${this.actionLabel(status, true).toLowerCase()}.`;
        this.$toast.success(message);
        this.liveMessage = message;
        await this.getPainel(false);
      } catch (error) {
        const message =
          error.response && error.response.data && error.response.data.msg
            ? error.response.data.msg
            : "Não foi possível atualizar o ticket.";
        this.$toast.error(message);
      } finally {
        this.$delete(this.updatingTicketKeys, ticket.key);
      }
    },
    updateTicketStatusFallback(itens, status) {
      return Promise.all(
        itens.map((ticketItem) =>
          this.$http.post("/restaurante/producao/update-status", {
            id: ticketItem.id,
            status,
          })
        )
      );
    },
    canTransitionTo(currentStatus, targetStatus) {
      const transitions = {
        pendente: ["preparando", "pronto"],
        preparando: ["pronto"],
        pronto: ["entregue"],
      };

      return (transitions[currentStatus] || []).includes(targetStatus);
    },
    itemMesaLabel(item) {
      if (item.mesa) {
        return `Mesa ${item.mesa.numero}`;
      }

      const comanda = item.item?.comanda || item.comanda;

      if (this.restauranteUsarApenasComandas && comanda?.tipo === "avulso") {
        return this.comandaModoLabel(comanda);
      }

      if (comanda?.tipo === "avulso" || comanda?.codigo) {
        return `Avulsa ${comanda.codigo || comanda.id}`;
      }

      return "Mesa não informada";
    },
    itemSetorLabel(item) {
      return item.setor_producao ? item.setor_producao.nome : "Sem setor";
    },
    itemTicketLabel(item) {
      const atendimento = item.atendimento_id
        ? `Atendimento #${item.atendimento_id}`
        : "Atendimento";

      return atendimento;
    },
    minutosDecorridos(item) {
      const value = this.itemStageDate(item);

      if (!value) {
        return 0;
      }

      const startedAt = new Date(value);
      const diff = Date.now() - startedAt.getTime();
      return Number.isFinite(diff) ? Math.max(diff / 60000, 0) : 0;
    },
    tempoDecorridoLabel(item) {
      return formatElapsedMinutes(this.minutosDecorridos(item));
    },
    isItemAtencao(item) {
      return (
        this.minutosDecorridos(item) >=
        restauranteTempoLimites.producaoAtencaoMinutos
      );
    },
    isItemAtrasado(item) {
      return (
        this.minutosDecorridos(item) >=
        restauranteTempoLimites.producaoAtrasoMinutos
      );
    },
    itemTimestamp(item) {
      const value = this.itemStageDate(item);

      if (!value) {
        return Date.now();
      }

      const timestamp = new Date(value).getTime();
      return Number.isFinite(timestamp) ? timestamp : Date.now();
    },
    itemStageDate(item) {
      if (item.status === "pronto" && item.pronto_at) {
        return item.pronto_at;
      }

      if (item.status === "preparando" && item.preparando_at) {
        return item.preparando_at;
      }

      return (
        item.enviado_at ||
        item.created_at ||
        item.updated_at ||
        item.item?.created_at
      );
    },
    produtoDescricao(item) {
      return item.item && item.item.produto
        ? item.item.produto.descricao
        : `Produto #${item.item ? item.item.produto_id : "-"}`;
    },
    quantidadeItem(item) {
      if (!item.item) {
        return "-";
      }

      const unidade =
        item.item.produto &&
        item.item.produto.unidade_medida &&
        item.item.produto.unidade_medida.sigla
          ? item.item.produto.unidade_medida.sigla
          : "";

      return `${Number(item.item.quantidade || 0).toLocaleString(
        "pt-BR"
      )} ${unidade}`;
    },
    observacaoLinhas(item) {
      const observacao = String(item.observacao || "")
        .replace(/\s*\|\s*\[pedido_online[^\]]+\]/g, "")
        .replace(/\[pedido_online[^\]]+\]/g, "")
        .trim();

      if (!observacao) {
        return [];
      }

      return observacao
        .split(/\s*\|\s*/)
        .map((linha) => linha.trim())
        .filter(Boolean);
    },
    actionLabel(status, ticket = false) {
      const itemLabels = {
        preparando: "Iniciar preparo",
        pronto: "Marcar pronto",
        entregue: "Retirar este item",
      };
      const ticketLabels = {
        preparando: "Iniciar preparo",
        pronto: "Marcar ticket pronto",
        entregue: "Retirar da tela",
      };

      return ticket
        ? ticketLabels[status] || this.statusLabel(status)
        : itemLabels[status] || this.statusLabel(status);
    },
    statusLabel(status) {
      const labels = {
        pendente: "Pendente",
        preparando: "Preparando",
        pronto: "Pronto",
        entregue: "Retirado",
      };

      return labels[status] || status;
    },
    ticketActionClass(status) {
      const classes = {
        preparando: "btn-warning",
        pronto: "btn-primary",
        entregue: "btn-success",
      };

      return classes[status] || "btn-light-primary";
    },
    columnHelp(status) {
      const labels = {
        pendente: "Aguardando início",
        preparando: "Em execução",
        pronto: "Aguardando retirada",
      };

      return labels[status] || "Fluxo de produção";
    },
    itemCountLabel(value) {
      return quantityLabel(value, "item", "itens");
    },
    isTicketUpdating(ticket) {
      return !!this.updatingTicketKeys[ticket.key];
    },
    isItemUpdating(item) {
      return !!this.updatingItemIds[item.id];
    },
    tipoLabel(tipo) {
      const labels = {
        cozinha: "Cozinha",
        bar: "Bar",
        outro: "Outro",
      };

      return labels[tipo] || tipo;
    },
    formatDate(value) {
      if (!value) {
        return "-";
      }

      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return "-";

      const today = new Date();
      const sameDay =
        date.getFullYear() === today.getFullYear() &&
        date.getMonth() === today.getMonth() &&
        date.getDate() === today.getDate();

      return sameDay
        ? `às ${date.toLocaleTimeString("pt-BR", {
            hour: "2-digit",
            minute: "2-digit",
          })}`
        : `em ${date.toLocaleDateString("pt-BR", {
            day: "2-digit",
            month: "2-digit",
          })} às ${date.toLocaleTimeString("pt-BR", {
            hour: "2-digit",
            minute: "2-digit",
          })}`;
    },
  },
};
